/**
 * AthleteSchedulePanel — per-athlete schedule editor.
 *
 * On the Schedule tab of the athlete dossier. Coach picks day + time
 * from existing schedule slots, then chooses recurring (auto-materializes
 * every week for the extent of their package) vs non-recurring (one-time
 * booking on a specific date). Lists both active recurring templates and
 * upcoming one-off bookings.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    CalendarClock, Plus, X, Loader2, Repeat, CalendarDays, ExternalLink, ClipboardList, Minus,
} from "lucide-react";
import { LogPastAttendanceModal } from "@/components/coach/PackPricingMatrix";
import { MoveBookingModal } from "@/components/coach/schedule/SlotBookingsModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function fmtTime12(hhmm) {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

function dowFromDateStr(dateStr) {
    // Returns 0=Mon..6=Sun
    const dt = new Date(`${dateStr}T00:00:00Z`);
    return (dt.getUTCDay() + 6) % 7;
}

export default function AthleteSchedulePanel({ athleteId, athleteFirstName }) {
    const [templates, setTemplates] = useState([]);
    const [oneOffs, setOneOffs] = useState([]);
    const [balance, setBalance] = useState(null); // { remaining, total, pack_label, expires_at }
    const [allPacks, setAllPacks] = useState([]); // every pack incl. consumed — history list
    // hasEntitlement = true if this athlete has ANY active entitlement
    // (pack OR subscription OR commitment). Used to suppress the false
    // "No active session pack" warning on subscriber-only members.
    // Sachin spec 2026-02-17: "Only show this when someone FOR SURE has
    // no sessions."
    const [hasEntitlement, setHasEntitlement] = useState(false);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [logPastOpen, setLogPastOpen] = useState(false);
    const [reloadTick, setReloadTick] = useState(0);
    const [movingBooking, setMovingBooking] = useState(null); // one-off being changed
    // Sachin 2026-03-06 — inline +/- for session-count adjustments on
    // the athlete-dossier schedule tab. Optimistic UI: bump the number
    // instantly, hit the server, reconcile from response, revert on
    // error.
    const [adjusting, setAdjusting] = useState(false);

    const adjustBalance = async (delta) => {
        // delta is expressed in coach terms (screen-side): +1 = give a
        // session back, -1 = take one away. The pack.adjust endpoint
        // works on pack.sessions_used which has INVERSE semantics —
        // we flip the sign before sending.
        if (!balance?.pack_id || adjusting) return;
        const prev = balance;
        const optimistic = {
            ...prev,
            remaining: Math.max(0, prev.remaining + delta),
            used: Math.max(0, prev.used - delta),
        };
        setBalance(optimistic);
        setAdjusting(true);
        try {
            const r = await axios.post(
                `${API}/coach/packs/${balance.pack_id}/adjust`,
                { delta: -delta, reason: null },   // invert for pack.sessions_used
                { withCredentials: true },
            );
            const serverUsed = typeof r.data?.sessions_used === "number" ? r.data.sessions_used : null;
            if (serverUsed !== null) {
                setBalance((b) => (b ? {
                    ...b,
                    used: serverUsed,
                    remaining: Math.max(0, (b.total || 0) - serverUsed),
                } : b));
            }
            toast.success(
                delta > 0
                    ? `+1 session on ${athleteFirstName || "athlete"}`
                    : `−1 session on ${athleteFirstName || "athlete"}`,
                { description: prev.pack_label ? `Applied to “${prev.pack_label}”` : undefined },
            );
        } catch (e) {
            setBalance(prev);   // revert
            const detail = e?.response?.data?.detail;
            const msg = typeof detail === "object" ? (detail.message || detail.code) : (detail || e?.message);
            toast.error("Adjustment failed", { description: typeof msg === "string" ? msg : "Unknown error" });
        } finally {
            setAdjusting(false);
        }
    };

    // LogPastAttendanceModal expects an array of {athlete_id, first_name, last_name}.
    // Pin to this single athlete so a coach can't accidentally log against
    // the wrong kid from inside their schedule.
    const logPastAthletes = useMemo(() => (
        athleteId ? [{
            athlete_id: athleteId,
            first_name: athleteFirstName || "Athlete",
            last_name: "",
        }] : []
    ), [athleteId, athleteFirstName]);

    // Cancel one specific upcoming booked date, straight from the profile.
    const cancelOneOff = async (b) => {
        if (!window.confirm(`Cancel this booking on ${b.date} at ${fmtTime12(b.start_time)}?`)) return;
        try {
            await axios.delete(`${API}/coach/schedule/bookings/${b.booking_id}`, { withCredentials: true });
            toast.success("Booking cancelled");
            setReloadTick((t) => t + 1);
        } catch (e) {
            toast.error("Cancel failed", { description: e?.response?.data?.detail });
        }
    };

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                // Fetch athlete to discover the parent_user_id, then pull packs via /coach/leads
                const athRow = await axios.get(`${API}/coach/athletes/${athleteId}`, { withCredentials: true });
                const parentUid = athRow.data?.athlete?.account_user_id || athRow.data?.parent?.user_id;
                const [rec, oo, lead, subStatus] = await Promise.all([
                    axios.get(`${API}/coach/athletes/${athleteId}/recurring`, { withCredentials: true }),
                    axios.get(`${API}/coach/attendance/athlete/${athleteId}?days=90`, { withCredentials: true }),
                    parentUid
                        ? axios.get(`${API}/coach/leads/${parentUid}`, { withCredentials: true })
                            .catch(() => ({ data: null }))
                        : Promise.resolve({ data: null }),
                    parentUid
                        ? axios.get(`${API}/coach/clients/${parentUid}/subscription-status`, { withCredentials: true })
                            .catch(() => ({ data: null }))
                        : Promise.resolve({ data: null }),
                ]);
                if (!alive) return;
                setTemplates(rec.data || []);
                const todayISO = new Date().toISOString().slice(0, 10);
                const upcoming = (oo.data?.log || [])
                    .filter((r) => r.status === "booked" && r.date >= todayISO && !r.is_manual_log)
                    .sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
                setOneOffs(upcoming);
                // Pull the highest-remaining ACTIVE pack from the parent's billing.
                const everyPack = lead.data?.active_packs || [];
                setAllPacks(everyPack);
                const packs = everyPack.filter((p) => p.status === "active");
                if (packs.length > 0) {
                    const best = packs.reduce((acc, p) => {
                        const rem = Math.max(0, (p.sessions_total || 0) - (p.sessions_used || 0));
                        if (!acc || rem > acc.remaining) {
                            return {
                                // Sachin 2026-03-06 — retain pack_id so the
                                // inline +/- arrows can hit the adjust
                                // endpoint directly.
                                pack_id: p.pack_id,
                                remaining: rem,
                                total: p.sessions_total || 0,
                                used: p.sessions_used || 0,
                                pack_label: p.label || p.pack_key,
                                expires_at: p.expires_at,
                            };
                        }
                        return acc;
                    }, null);
                    setBalance(best);
                } else {
                    setBalance(null);
                }
                // Compute entitlement: ANY active pack, subscription, or
                // active commitment counts. The warning is only meaningful
                // when ALL of these are zero. Otherwise the athlete already
                // has a way to attend that doesn't require a pack.
                const hasSub = !!(subStatus.data?.active);
                const hasActiveCommit = (lead.data?.active_commitments || []).some(
                    (c) => c?.status === "active",
                );
                setHasEntitlement(packs.length > 0 || hasSub || hasActiveCommit);
            } catch (e) {
                toast.error("Couldn't load schedule", { description: e?.response?.data?.detail });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [athleteId, reloadTick]);

    const removeTemplate = async (templateId) => {
        if (!window.confirm("Remove this recurring slot? All future bookings from it will be cancelled.")) return;
        try {
            await axios.delete(`${API}/coach/recurring/${templateId}`, { withCredentials: true });
            toast.success("Recurring slot removed");
            setReloadTick((t) => t + 1);
        } catch (e) {
            toast.error("Couldn't remove", { description: e?.response?.data?.detail });
        }
    };

    const [resyncing, setResyncing] = useState(false);
    const resyncRecurring = async () => {
        setResyncing(true);
        try {
            const r = await axios.post(
                `${API}/coach/athletes/${athleteId}/recurring/resync`,
                {}, { withCredentials: true },
            );
            const n = r.data?.bookings_created || 0;
            toast.success(n > 0 ? `Re-booked ${n} missing week${n === 1 ? "" : "s"}` : "Recurring already up to date");
            setReloadTick((t) => t + 1);
        } catch (e) {
            toast.error("Re-sync failed", { description: e?.response?.data?.detail });
        } finally {
            setResyncing(false);
        }
    };

    if (loading) {
        return <div className="py-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
    }

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-white inline-flex items-center gap-2">
                        <CalendarClock className="w-5 h-5 text-brand-red" />
                        {athleteFirstName || "Athlete"}&apos;s schedule
                    </h2>
                    <p className="text-zinc-400 text-sm mt-1">
                        Add weekly recurring slots (auto-booked throughout their package) or one-time bookings on a specific date.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        data-testid="schedule-log-past-btn"
                        onClick={() => setLogPastOpen(true)}
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase border border-amber-600/60 bg-amber-950/30 text-amber-200 px-2.5 py-2 hover:bg-amber-900/50 transition-colors"
                        title="Record a session that already happened but wasn't on the schedule"
                    >
                        <ClipboardList className="w-3.5 h-3.5" /> Log past session
                    </button>
                    <button
                        type="button"
                        data-testid="schedule-add-slot"
                        onClick={() => setAddOpen(true)}
                        className="btn-tactical inline-flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" /> Add slot
                    </button>
                </div>
            </div>

            {logPastOpen && logPastAthletes.length > 0 && (
                <LogPastAttendanceModal
                    athletes={logPastAthletes}
                    onClose={() => setLogPastOpen(false)}
                    onLogged={() => { setLogPastOpen(false); setReloadTick((t) => t + 1); }}
                />
            )}

            {/* Session balance pill — coach uses this to gauge how many recurring
                weeks the athlete can commit to before they run out of sessions. */}
            {balance && (
                <div
                    data-testid="schedule-balance-pill"
                    className="border border-zinc-800 bg-zinc-950 rounded-2xl px-5 py-4 flex items-center justify-between flex-wrap gap-3"
                >
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            {/* Sachin 2026-03-06 — inline session +/-.
                                Coach can bump the balance up/down without
                                leaving this tab. Optimistic + toast. */}
                            <button
                                type="button"
                                data-testid="schedule-balance-decrement"
                                onClick={() => adjustBalance(-1)}
                                disabled={adjusting || !balance.pack_id || balance.remaining <= 0}
                                aria-label="Take one session away"
                                title="Take one session away"
                                className="w-8 h-8 inline-flex items-center justify-center rounded border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                <Minus className="w-4 h-4" />
                            </button>
                            <div className="text-center min-w-[3rem]">
                                <div
                                    data-testid="schedule-balance-number"
                                    className={`text-3xl font-bold ${balance.remaining <= 2 ? "text-rose-400" : balance.remaining <= 5 ? "text-amber-300" : "text-emerald-300"}`}
                                >
                                    {adjusting ? <Loader2 className="w-6 h-6 mx-auto animate-spin" /> : balance.remaining}
                                </div>
                                <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500">Left</div>
                            </div>
                            <button
                                type="button"
                                data-testid="schedule-balance-increment"
                                onClick={() => adjustBalance(+1)}
                                disabled={adjusting || !balance.pack_id || balance.used <= 0}
                                aria-label="Give one session back"
                                title="Give one session back"
                                className="w-8 h-8 inline-flex items-center justify-center rounded border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="h-10 w-px bg-zinc-800" />
                        <div>
                            <div className="text-sm text-white">{balance.pack_label}</div>
                            <div className="text-xs text-zinc-400 mt-0.5">
                                {balance.remaining} of {balance.total} sessions remaining
                                {balance.expires_at ? ` · expires ${new Date(balance.expires_at).toLocaleDateString()}` : ""}
                            </div>
                        </div>
                    </div>
                    {/* Coach hint: weeks at current cadence */}
                    {templates.length > 0 && (
                        <div className="text-xs text-zinc-400">
                            At {templates.length}× / week → <span className="text-white">~{Math.max(0, Math.floor(balance.remaining / templates.length))} weeks</span>
                        </div>
                    )}
                </div>
            )}
            {!balance && !loading && !hasEntitlement && (
                <div className="border border-amber-700/40 bg-amber-950/20 rounded-2xl px-5 py-3 text-amber-200 text-sm">
                    ⚠ No active session pack. Add one before scheduling so check-ins deduct properly.
                </div>
            )}
            {!balance && !loading && hasEntitlement && (
                <div className="border border-emerald-700/40 bg-emerald-950/20 rounded-2xl px-5 py-3 text-emerald-200 text-sm" data-testid="athlete-sub-entitlement-banner">
                    ✓ Active subscription — sessions auto-credit. No pack required.
                </div>
            )}

            {/* Package history — EVERY pack this family bought (incl. used-up
                ones) with its own count, so past 1-packs etc. stay visible.
                Sachin 2026-07: "I only see the 20 pack — make sure previous
                packages and sessions used show up there as well." */}
            {allPacks.length > 0 && (
                <section data-testid="schedule-pack-history" className="border border-zinc-800 bg-zinc-950 rounded-2xl px-5 py-4">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2.5">
                        Packages · {allPacks.length}
                    </div>
                    <div className="divide-y divide-zinc-900">
                        {allPacks.map((p) => {
                            const total = Number(p.sessions_total || 0);
                            const used = Number(p.sessions_used || 0);
                            const rem = Math.max(0, total - used);
                            const st = p.status || "active";
                            const badge = {
                                active: ["Active", "text-emerald-300 border-emerald-800/60 bg-emerald-950/30"],
                                consumed: ["Used up", "text-zinc-400 border-zinc-700 bg-zinc-900/40"],
                                pending: ["Pending payment", "text-amber-300 border-amber-800/60 bg-amber-950/30"],
                                expired: ["Expired", "text-zinc-500 border-zinc-800 bg-zinc-900/40"],
                            }[st] || [st, "text-zinc-400 border-zinc-700 bg-zinc-900/40"];
                            return (
                                <div
                                    key={p.pack_id}
                                    data-testid={`pack-history-row-${p.pack_id}`}
                                    className="py-2 flex items-center justify-between gap-3 flex-wrap"
                                >
                                    <div className="min-w-0">
                                        <div className={`text-sm truncate ${st === "active" ? "text-white" : "text-zinc-400"}`}>
                                            {p.label || p.pack_key || "Session pack"}
                                        </div>
                                        <div className="text-[11px] text-zinc-500 mt-0.5">
                                            {used} of {total} used
                                            {p.purchased_at ? ` · bought ${new Date(p.purchased_at).toLocaleDateString()}` : ""}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2.5 flex-shrink-0">
                                        <span className={`text-sm font-mono ${rem > 0 ? "text-emerald-300" : "text-zinc-500"}`}>
                                            {rem} left
                                        </span>
                                        <span className={`text-[9px] tracking-[0.15em] uppercase border px-1.5 py-0.5 rounded ${badge[1]}`}>
                                            {badge[0]}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Recurring */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs tracking-[0.3em] uppercase text-zinc-400 inline-flex items-center gap-2">
                        <Repeat className="w-4 h-4" /> Recurring · {templates.length}
                    </h3>
                    {templates.length > 0 && (
                        <button
                            type="button"
                            data-testid="recurring-resync"
                            onClick={resyncRecurring}
                            disabled={resyncing}
                            title="Re-book any missing upcoming weeks for these recurring slots"
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-brand-red hover:text-white transition-colors disabled:opacity-50"
                        >
                            {resyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Repeat className="w-3 h-3" />}
                            Re-sync
                        </button>
                    )}
                </div>
                {templates.length === 0 ? (
                    <div className="border border-zinc-900 bg-zinc-950/40 rounded-2xl px-6 py-8 text-center text-zinc-500 text-sm">
                        No recurring slots yet. Click <span className="text-white">+ Add slot</span> above.
                    </div>
                ) : (
                    <div className="border border-zinc-900 bg-zinc-950/40 rounded-2xl divide-y divide-zinc-900">
                        {templates.map((t) => (
                            <div key={t.template_id} data-testid={`recurring-${t.template_id}`} className="px-4 py-3 flex items-center justify-between">
                                <div>
                                    <div className="text-white font-medium">
                                        {DAY_LABELS[t.day_of_week]} · {fmtTime12(t.start_time)}
                                    </div>
                                    {t.label && (
                                        <div className="text-zinc-400 text-xs mt-0.5">{t.label}{t.category ? ` · ${t.category}` : ""}</div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    data-testid={`recurring-remove-${t.template_id}`}
                                    onClick={() => removeTemplate(t.template_id)}
                                    className="text-zinc-500 hover:text-rose-400 p-1.5 -m-1.5"
                                    aria-label="Remove recurring slot"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* One-off upcoming bookings */}
            <section>
                <h3 className="text-xs tracking-[0.3em] uppercase text-zinc-400 mb-3 inline-flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" /> Upcoming one-time bookings · {oneOffs.length}
                </h3>
                {oneOffs.length === 0 ? (
                    <div className="border border-zinc-900 bg-zinc-950/40 rounded-2xl px-6 py-8 text-center text-zinc-500 text-sm">
                        No upcoming one-time bookings.
                    </div>
                ) : (
                    <div className="border border-zinc-900 bg-zinc-950/40 rounded-2xl divide-y divide-zinc-900">
                        {oneOffs.map((b) => (
                            <div key={b.booking_id} className="px-4 py-3 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-white font-medium">
                                        {new Date(`${b.date}T00:00:00Z`).toLocaleDateString(undefined, {
                                            weekday: "short", month: "short", day: "numeric", timeZone: "UTC",
                                        })} · {fmtTime12(b.start_time)}
                                    </div>
                                    {b.label && <div className="text-zinc-400 text-xs mt-0.5">{b.label}</div>}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        type="button"
                                        data-testid={`oneoff-change-${b.booking_id}`}
                                        onClick={() => setMovingBooking(b)}
                                        className="text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 border border-zinc-700 text-zinc-200 hover:border-zinc-500"
                                    >
                                        Change
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`oneoff-cancel-${b.booking_id}`}
                                        onClick={() => cancelOneOff(b)}
                                        className="text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 text-zinc-400 hover:text-brand-red"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {addOpen && (
                <AddSlotModal
                    athleteId={athleteId}
                    onClose={() => setAddOpen(false)}
                    onAdded={() => { setAddOpen(false); setReloadTick((t) => t + 1); }}
                />
            )}

            {movingBooking && (
                <MoveBookingModal
                    booking={{ booking_id: movingBooking.booking_id, athlete_name: athleteFirstName }}
                    currentSlot={{
                        slot_id: movingBooking.slot_id,
                        date: movingBooking.date,
                        start_time: movingBooking.start_time,
                    }}
                    onClose={() => setMovingBooking(null)}
                    onConfirm={async (targetSlotId) => {
                        try {
                            await axios.post(
                                `${API}/coach/schedule/bookings/${movingBooking.booking_id}/move`,
                                { target_slot_id: targetSlotId },
                                { withCredentials: true },
                            );
                            toast.success("Moved to new slot");
                            setMovingBooking(null);
                            setReloadTick((t) => t + 1);
                        } catch (e) {
                            toast.error("Move failed", { description: e?.response?.data?.detail });
                        }
                    }}
                />
            )}
        </div>
    );
}

function AddSlotModal({ athleteId, onClose, onAdded }) {
    const [mode, setMode] = useState("recurring"); // "recurring" | "oneoff"
    const [dow, setDow] = useState(1); // default Mon
    const [date, setDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        return d.toISOString().slice(0, 10);
    });
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [selectedSlotId, setSelectedSlotId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // For recurring: dow drives the slot list. For oneoff: derived from date.
    const effectiveDow = mode === "recurring" ? dow : dowFromDateStr(date);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoadingSlots(true);
            setSelectedSlotId("");
            try {
                const r = await axios.get(
                    `${API}/coach/schedule/slots-by-day/${effectiveDow}`,
                    { withCredentials: true },
                );
                if (alive) setSlots(r.data || []);
            } catch (e) {
                toast.error("Couldn't load slots");
            } finally {
                if (alive) setLoadingSlots(false);
            }
        })();
        return () => { alive = false; };
    }, [effectiveDow]);

    // For one-off mode, filter slots to the specific date the user picked.
    const oneOffSlots = useMemo(() => {
        if (mode !== "oneoff") return [];
        // The slots-by-day endpoint returns the EARLIEST upcoming slot per
        // (time, label, category). For a specific date, we need any slot
        // actually on that date — fall back to fetching all slots for the
        // exact date if needed. But for the typical case (the date is in
        // the next 8 weeks), the time pattern dropdown is what matters —
        // the actual slot_id we use is the one matching the chosen date.
        return slots;
    }, [mode, slots]);

    const submit = async (e) => {
        e.preventDefault();
        if (!selectedSlotId) return;
        setSubmitting(true);
        try {
            if (mode === "recurring") {
                await axios.post(
                    `${API}/coach/athletes/${athleteId}/recurring`,
                    { slot_id: selectedSlotId },
                    { withCredentials: true },
                );
                toast.success("Recurring slot added", { description: "Auto-booking every week throughout their package." });
            } else {
                // For one-off, find the slot for the specific date the user picked.
                // The slots-by-day endpoint gave us one anchor per (time, label).
                // We need to fetch the EXACT slot for this date+time. Use a quick
                // call to /coach/schedule/slots filtered to the date.
                const day_slots_r = await axios.get(`${API}/coach/schedule/slots`, {
                    params: { from_date: date, to_date: date },
                    withCredentials: true,
                });
                const anchor = (slots.find((s) => s.slot_id === selectedSlotId)) || null;
                if (!anchor) throw new Error("Could not find selected slot");
                const exact = (day_slots_r.data || []).find((s) =>
                    s.date === date && s.start_time === anchor.start_time
                    && (s.label || "") === (anchor.label || "")
                    && (s.category || "") === (anchor.category || "")
                );
                if (!exact) {
                    toast.error("No slot exists on that exact date", {
                        description: "Open the Schedule page to publish that day from the template.",
                    });
                    return;
                }
                await axios.post(
                    `${API}/coach/athletes/${athleteId}/single-booking`,
                    { slot_id: exact.slot_id },
                    { withCredentials: true },
                );
                toast.success("One-time booking added");
            }
            onAdded();
        } catch (e) {
            toast.error("Couldn't add slot", { description: e?.response?.data?.detail || e.message });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="add-slot-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 w-full max-w-lg shadow-2xl"
            >
                <h3 className="font-display text-2xl tracking-wider text-white mb-1">Add a slot</h3>
                <p className="text-zinc-400 text-sm mb-6">Pick a day, a time, and how often.</p>

                {/* Recurring vs one-off toggle */}
                <div className="grid grid-cols-2 gap-2 mb-5">
                    <button
                        type="button"
                        data-testid="add-mode-recurring"
                        onClick={() => setMode("recurring")}
                        className={`text-xs tracking-wider uppercase border px-3 py-2.5 transition-colors inline-flex items-center justify-center gap-2 ${
                            mode === "recurring"
                                ? "border-brand-red bg-brand-red/10 text-white"
                                : "border-zinc-800 text-zinc-300 hover:border-zinc-700"
                        }`}
                    >
                        <Repeat className="w-4 h-4" /> Recurring · every week
                    </button>
                    <button
                        type="button"
                        data-testid="add-mode-oneoff"
                        onClick={() => setMode("oneoff")}
                        className={`text-xs tracking-wider uppercase border px-3 py-2.5 transition-colors inline-flex items-center justify-center gap-2 ${
                            mode === "oneoff"
                                ? "border-brand-red bg-brand-red/10 text-white"
                                : "border-zinc-800 text-zinc-300 hover:border-zinc-700"
                        }`}
                    >
                        <CalendarDays className="w-4 h-4" /> One-time
                    </button>
                </div>

                {/* Day or Date */}
                {mode === "recurring" ? (
                    <div className="mb-4">
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Day of week</label>
                        <select
                            data-testid="add-day-of-week"
                            value={dow}
                            onChange={(e) => setDow(Number(e.target.value))}
                            className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                        >
                            {DAY_LABELS.map((d, i) => (
                                <option key={i} value={i}>{d}</option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <div className="mb-4">
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Date</label>
                        <input
                            type="date"
                            data-testid="add-date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            min={new Date().toISOString().slice(0, 10)}
                            className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                        />
                        <p className="text-zinc-500 text-xs mt-1.5">
                            That&apos;s a {DAY_LABELS[dowFromDateStr(date)]}.
                        </p>
                    </div>
                )}

                {/* Time */}
                <div className="mb-6">
                    <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Time</label>
                    {loadingSlots ? (
                        <div className="text-zinc-500 text-sm">Loading available times…</div>
                    ) : slots.length === 0 ? (
                        <div className="border border-amber-700/40 bg-amber-950/20 rounded px-4 py-3 text-sm text-amber-200">
                            No times scheduled for {DAY_LABELS[effectiveDow]} in the next 8 weeks.
                            <Link
                                to="/coach/schedule"
                                data-testid="add-create-slot-link"
                                className="inline-flex items-center gap-1.5 text-white underline ml-1"
                            >
                                Open Schedule to add one <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>
                    ) : (
                        <select
                            data-testid="add-slot-select"
                            value={selectedSlotId}
                            onChange={(e) => setSelectedSlotId(e.target.value)}
                            required
                            className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                        >
                            <option value="">— Pick a time —</option>
                            {slots.map((s) => (
                                <option key={s.slot_id} value={s.slot_id}>
                                    {fmtTime12(s.start_time)}{s.label ? ` · ${s.label}` : ""}{s.category ? ` (${s.category})` : ""}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-zinc-400 hover:text-white text-xs tracking-wider uppercase px-3 py-2"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        data-testid="add-slot-submit"
                        disabled={!selectedSlotId || submitting}
                        className="btn-tactical inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {submitting ? "Adding…" : "Add slot"}
                    </button>
                </div>
            </form>
        </div>
    );
}
